// Saved-canvas (layout board) contract — FE2a. The app's OWN state: a user's
// arranged dashboard, stored in DynamoDB and scoped to the logged-in user —
// never the read-only Catalyst RDS (invariant §12.11).
//
// There are TWO independent canvases (PLAN §7B): 'leagues' and 'players'. A
// board is per (user × section), so each canvas saves and loads on its own.
// A board holds ONLY widget placements — filters live in the frontend URL +
// localStorage per section (§7B decision 4), not here.
//
// Pure: no AWS, no DOM. Shared by app-web (arranging widgets) and app-api
// (validating a board on write) so both run the same geometry + validation.

import {
  clampToGrid,
  type GridPosition,
  type WidgetPlacement,
} from "../grid/grid.js";

/** The two independent canvases. Each has its own saved layout board. */
export const LAYOUT_SECTIONS = ["leagues", "players"] as const;
export type LayoutSection = (typeof LAYOUT_SECTIONS)[number];

/** Fixed desktop grid width (columns). Mobile/responsive reflow is deferred —
 *  the demo is shown on a laptop (§7B). Every placement is clamped to this. */
export const GRID_COLUMNS = 12;

/** Guard rails so a single board can't grow unbounded (fail fast on write). */
export const MAX_PLACEMENTS = 50;
export const MAX_WIDGET_KEY_LEN = 100;

/** A saved board — the GET/PUT /layouts response shape. */
export interface LayoutBoard {
  section: LayoutSection;
  placements: WidgetPlacement[];
  /** ISO timestamp of the last save, or null if this section was never saved. */
  updatedAt: string | null;
}

/** Body of PUT /layouts. */
export interface LayoutUpdate {
  placements: WidgetPlacement[];
}

/** Discriminated parse result so a handler can return a precise 400 message. */
export type ParseResult<T> =
  | { ok: true; value: T }
  | { ok: false; error: string };

/** Validate a `section` query param, else an error (→ 400). Required — we never
 *  guess which canvas the caller means (fail-loud, §12.2). */
export function parseLayoutSection(
  raw: string | null | undefined
): ParseResult<LayoutSection> {
  if (!raw) {
    return { ok: false, error: "section is required (one of: leagues, players)" };
  }
  if ((LAYOUT_SECTIONS as readonly string[]).includes(raw)) {
    return { ok: true, value: raw as LayoutSection };
  }
  return { ok: false, error: "section must be one of: leagues, players" };
}

/** A grid position from untrusted input: all four fields must be finite numbers
 *  (rejects NaN/Infinity/strings). Bounds/whole-cell fixing is `clampToGrid`'s
 *  job, done by the caller — here we only ensure the shape is numeric. */
function parseGridPosition(raw: unknown): ParseResult<GridPosition> {
  if (typeof raw !== "object" || raw === null || Array.isArray(raw)) {
    return { ok: false, error: "placement.position must be an object { x, y, w, h }" };
  }
  const r = raw as Record<string, unknown>;
  for (const k of ["x", "y", "w", "h"] as const) {
    if (typeof r[k] !== "number" || !Number.isFinite(r[k])) {
      return { ok: false, error: `placement.position.${k} must be a finite number` };
    }
  }
  return {
    ok: true,
    value: { x: r.x as number, y: r.y as number, w: r.w as number, h: r.h as number },
  };
}

/** Validate ONE placement from untrusted input and clamp its geometry to the
 *  grid. `widgetKey` is stored opaque — the known-key allowlist is the frontend
 *  registry (FE2b); the backend only checks it's a sane non-empty string. */
function parsePlacement(raw: unknown): ParseResult<WidgetPlacement> {
  if (typeof raw !== "object" || raw === null || Array.isArray(raw)) {
    return { ok: false, error: "each placement must be an object" };
  }
  const p = raw as Record<string, unknown>;
  const widgetKey = p.widgetKey;
  if (typeof widgetKey !== "string" || widgetKey.trim() === "") {
    return { ok: false, error: "placement.widgetKey must be a non-empty string" };
  }
  if (widgetKey.length > MAX_WIDGET_KEY_LEN) {
    return { ok: false, error: `placement.widgetKey must be ${MAX_WIDGET_KEY_LEN} characters or fewer` };
  }
  const pos = parseGridPosition(p.position);
  if (!pos.ok) return pos;
  // clampToGrid pins x/y/w/h to whole, in-bounds cells — so nothing
  // out-of-bounds or fractional is ever stored (fail-safe geometry).
  return { ok: true, value: { widgetKey, position: clampToGrid(pos.value, GRID_COLUMNS) } };
}

/**
 * Validate + clamp a raw PUT /layouts body into `{ placements }`, else an error
 * (→ 400). STRICT: any malformed placement fails the whole write (fail fast,
 * §12.2) — a client bug should surface, not silently drop widgets. Pure —
 * unit-tested without DynamoDB.
 */
export function parseLayoutBody(
  raw: string | null | undefined
): ParseResult<LayoutUpdate> {
  if (!raw) return { ok: false, error: "request body is required" };
  let body: unknown;
  try {
    body = JSON.parse(raw);
  } catch {
    return { ok: false, error: "body must be valid JSON" };
  }
  if (typeof body !== "object" || body === null || Array.isArray(body)) {
    return { ok: false, error: "body must be a JSON object { placements: [...] }" };
  }
  const placements = (body as Record<string, unknown>).placements;
  if (!Array.isArray(placements)) {
    return { ok: false, error: "placements must be an array" };
  }
  if (placements.length > MAX_PLACEMENTS) {
    return { ok: false, error: `placements must be ${MAX_PLACEMENTS} or fewer` };
  }
  const out: WidgetPlacement[] = [];
  for (const p of placements) {
    const parsed = parsePlacement(p);
    if (!parsed.ok) return parsed;
    out.push(parsed.value);
  }
  return { ok: true, value: { placements: out } };
}

/**
 * Coerce stored placements (an `unknown` read back from DynamoDB) into a clean
 * `WidgetPlacement[]`. LENIENT — unlike the write path, malformed items are
 * DROPPED, not rejected: a GET must never 500 on an old or corrupt board.
 * Good items are re-clamped to the current grid. Pure — unit-tested.
 */
export function sanitizePlacements(raw: unknown): WidgetPlacement[] {
  if (!Array.isArray(raw)) return [];
  const out: WidgetPlacement[] = [];
  for (const p of raw) {
    const parsed = parsePlacement(p);
    if (parsed.ok) out.push(parsed.value);
  }
  return out;
}
